import { useState, useEffect, useRef } from "react";

// Intersection Observer hook to trigger animation on visibility
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

// Use the same images/format as HeroSection
const aboutImages = [
  {
    src: "/img/img1.jpeg",
    alt: "Sample Computer Repair",
  },
  {
    src: "/img/img2.jpeg",
    alt: "Laptop/Server Service",
  },
  {
    src: "/img/img3.jpeg",
    alt: "CCTV/Projector Install",
  },
  {
    src: "/img/img4.jpeg",
    alt: "IT Service",
  },
  {
    src: "/img/img5.jpeg",
    alt: "IT Professional",
  },
  {
    src: "/img/img6.jpeg",
    alt: "Laptop Fixing",
  },
  {
    src: "/img/img7.jpeg",
    alt: "Sample Computer Repair",
  },
];

export default function AboutIntro() {
  const [current, setCurrent] = useState(0);
  const [fade, setFade] = useState(true);

  // Animation trigger
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // When section is visible, trigger animation
  const isVisible = useOnScreen(sectionRef, "-70px");

  // Handles cascading/fade-in animation for section entry
  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  // Fading image slider logic (does not run if hidden/no animation yet)
  const timeoutRef = useRef(null);
  useEffect(() => {
    if (!hasAnimated) return; // Don't animate slider until section is visible!

    setFade(true);
    timeoutRef.current = setTimeout(() => {
      setFade(false);
      timeoutRef.current = setTimeout(() => {
        setCurrent((c) => (c + 1) % aboutImages.length);
        setFade(true);
      }, 350); // fade out duration
    }, 2000); // show image for 2 seconds

    return () => clearTimeout(timeoutRef.current);
  }, [current, hasAnimated]);

  const handleDotClick = (idx) => {
    setCurrent(idx);
    setFade(true);
    clearTimeout(timeoutRef.current);
  };

  // Animation style utilities
  const transitionBase = `all 1200ms cubic-bezier(0.33,0.75,0.68,1)`;
  const slideAnim = (delay = 0, y = 32) => ({
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated ? "translateY(0)" : `translateY(${y}px)`,
    transition: `${transitionBase}`,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    willChange: "opacity, transform",
  });

  // Heading, divider, paragraphs, stats: add stagger delays for smooth in
  return (
    <section
      className="py-24 bg-white font-PTSerif-Regular"
      ref={sectionRef}
    >
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center font-PTSerif-Regular">
        {/* Slider Image with Fade Animation */}
        <div
          className="relative w-full h-[330px] md:h-[400px] flex items-center justify-center font-PTSerif-Regular"
          style={slideAnim(0, 26)} // image comes in slightly later and from below
        >
          <img
            src={aboutImages[current].src}
            alt={aboutImages[current].alt}
            className={`rounded-lg shadow-lg w-full h-full object-cover transition-opacity duration-350 ${
              fade ? "opacity-100" : "opacity-0"
            } font-PTSerif-Regular`}
            style={{
              aspectRatio: "4/3",
            }}
          />
          {/* Dots for manual control */}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 font-PTSerif-Regular">
            {aboutImages.slice(0, 7).map((_, idx) => (
              <button
                key={idx}
                onClick={() => handleDotClick(idx)}
                className={`h-2 w-2 rounded-full border-2 transition ${
                  current === idx
                    ? "bg-blue-500 border-blue-500"
                    : "bg-white border-gray-300"
                } font-PTSerif-Regular`}
                tabIndex={-1}
                aria-label={`Show image ${idx + 1}`}
                style={{ outline: "none" }}
              ></button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="font-PTSerif-Regular" style={slideAnim(120, 42)}>
          <h1
            className="text-4xl font-bold text-gray-900 font-PTSerif-Regular"
            style={slideAnim(220, 36)}
          >
            About STRALIA PRIVATE LIMITED
          </h1>

          <div
            className="w-16 h-[3px] bg-blue-500 mt-4 mb-6 font-PTSerif-Regular"
            style={slideAnim(340, 18)}
          ></div>

          <p
            className="text-gray-600 leading-relaxed mb-6 font-PTSerif-Regular"
            style={slideAnim(400, 24)}
          >
            STRALIA PRIVATE LIMITED is a trusted IT services provider delivering
            professional AMC solutions, system maintenance, network management,
            and security infrastructure for modern businesses.
          </p>

          <p
            className="text-gray-600 leading-relaxed mb-6 font-PTSerif-Regular"
            style={slideAnim(480, 20)}
          >
            Since 2014, our team has helped organizations maintain reliable IT
            environments by offering preventive maintenance, technical support,
            installation services, and rapid troubleshooting across multiple
            technologies.
          </p>

          <p
            className="text-gray-600 leading-relaxed font-PTSerif-Regular"
            style={slideAnim(560, 14)}
          >
            We work with businesses of all sizes to ensure their systems,
            networks, and surveillance infrastructure operate efficiently,
            securely, and without disruption.
          </p>

          <div
            className="mt-8 grid grid-cols-2 gap-6 text-center font-PTSerif-Regular"
            style={slideAnim(700, 18)}
          >
            <div className="bg-gray-100 p-6 rounded-lg font-PTSerif-Regular" style={slideAnim(830, 20)}>
              <h3 className="text-3xl font-bold text-blue-600 font-PTSerif-Regular">10+</h3>
              <p className="text-sm text-gray-600 font-PTSerif-Regular">Years Industry Experience</p>
            </div>
            <div className="bg-gray-100 p-6 rounded-lg font-PTSerif-Regular" style={slideAnim(950, 20)}>
              <h3 className="text-3xl font-bold text-blue-600 font-PTSerif-Regular">2000+</h3>
              <p className="text-sm text-gray-600 font-PTSerif-Regular">Businesses Supported</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}